/**
 * Compute (OpenNebula) Business Usage hook — the per-flavor billing-basis read.
 *
 *   GET /api/v3/services/:id/compute/business?from&to&granularity=day|month
 *
 * Mirrors the CDN service-scoped `useServiceCdnBusiness` (src/api/manticore/service-cdn.ts):
 * user-JWT via `serviceApiV3`, envelope body `{ data: { usages } }`, `retry:false`,
 * errors swallowed to `[]` so an empty/missing period renders EmptyState (not an error).
 *
 * Each `usages` row is already a per-flavor total (instance-months + disk GB-months
 * derived on read); no {name,values} adapter is needed.
 */
import { useQuery } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { type Envelope } from '@/api/manticore/_shared';

/** One flavor's aggregated usage for the period. */
export interface FlavorUsageRow {
  flavor: string;
  resource_type: string;
  instance_months: number;
  disk_gb_months: number;
  vcpu: number;
  ram_gb: number;
}

interface ComputeBusinessResult {
  service: { id: string };
  from: string;
  to: string;
  granularity: string;
  usages: FlavorUsageRow[];
}

export interface ComputeBusinessParams {
  from: string;
  to: string;
  granularity?: string;
}

export function useComputeBusinessUsage(
  serviceId: string | undefined,
  params: ComputeBusinessParams | null,
) {
  const { from = '', to = '', granularity } = params ?? {};
  return useQuery({
    queryKey: ['compute', 'service', serviceId, 'business', from, to, granularity ?? ''],
    enabled: !!serviceId && !!params,
    retry: false,
    queryFn: async (): Promise<FlavorUsageRow[]> => {
      const p = new URLSearchParams({ from, to });
      if (granularity) p.set('granularity', granularity);
      try {
        const res = await serviceApiV3.get<Envelope<ComputeBusinessResult>>(
          `/services/${serviceId}/compute/business?${p.toString()}`,
        );
        return res.data?.data?.usages ?? [];
      } catch {
        return [];
      }
    },
  });
}
